import { useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import { match } from 'ts-pattern'
import { TooltipItem } from 'chart.js'
import { Text } from '@/components'
import { BarGraph, BarGraphDataset } from '@/components/BarGraph/V2/BarGraph'
import { HourlyCost, RatePeriod } from '@/types/plugSessions'

const PERIOD_COLORS: Record<RatePeriod, string> = {
  flat: '#00AB32',
  off_peak: '#00AB32',
  partial_peak: '#FBB142',
  peak: '#FB5842',
  // Real-time cost categories reuse the same palette: cheap is green,
  // expensive is red; negative-cost hours also read green (bars dip below
  // the axis, which is distinction enough)
  negative: '#00AB32',
  low: '#00AB32',
  medium: '#FBB142',
  high: '#FB5842',
}

const CHART_PERIODS = [
  'off_peak',
  'partial_peak',
  'peak',
  'negative',
  'low',
  'medium',
  'high',
] as const

export interface HourlyRateChartProps {
  hourlyCosts: HourlyCost[]
  currency: string
  chartHeight?: number
}

/**
 * Hourly cost bar chart for a plug session, colored by rate period
 * (green = off peak, orange = partial peak, red = peak). Flat sessions
 * classify uniformly and render all-green. Wraps the BarGraph V2
 * component also used by the thermostat Save Money page.
 */
export default function HourlyRateChart({
  hourlyCosts,
  currency,
  chartHeight,
}: HourlyRateChartProps) {
  // Bar height is the cost of that hour's charging; color encodes the rate
  // period, so expensive (peak) hours read as tall red bars
  const datasets = useMemo(
    () =>
      CHART_PERIODS.map(
        (period): BarGraphDataset => ({
          data: hourlyCosts.map((bar) =>
            bar.period === period ||
            (period === 'off_peak' && bar.period === 'flat')
              ? bar.cost
              : null,
          ),
          backgroundColor: PERIOD_COLORS[period],
        }),
      ),
    [hourlyCosts],
  )

  // Scale the axis to the most expensive hour. Once hours cost $1+, the top
  // is the NEXT WHOLE DOLLAR up (a $4.30 hour tops out at $5, never $6) with
  // $1 ticks; below $1, cents remain the only readable scale ($0.15 can't
  // round to $0)
  const maxCost = useMemo(
    () => Math.max(...hourlyCosts.map((bar) => bar.cost), 0),
    [hourlyCosts],
  )
  const wholeDollars = maxCost >= 1
  const step = wholeDollars ? 1 : (maxCost > 0 ? maxCost : 1) / 2
  const axisTop = wholeDollars ? Math.ceil(maxCost) : step * 2
  const yAxisValues = useMemo(() => [0, axisTop / 2, axisTop], [axisTop])

  // X-axis shows only the start and end of the charge time range. When the
  // session crosses midnight, "2pm … 1am" alone misreads as a same-day range,
  // so the endpoint labels pick up the day (e.g. "2pm Mon … 1am Wed").
  const labels = useMemo(() => {
    if (!hourlyCosts.length) {
      return []
    }
    const first = dayjs(hourlyCosts[0].hour_starting)
    const last = dayjs(hourlyCosts[hourlyCosts.length - 1].hour_starting)
    const crossesMidnight = !first.isSame(last, 'day')
    const formatEndpoint = (d: dayjs.Dayjs) =>
      crossesMidnight
        ? `${d.format('ha').toLowerCase()} ${d.format('ddd')}`
        : d.format('ha').toLowerCase()
    return hourlyCosts.map((bar, index, all) =>
      match(index)
        .with(0, () => formatEndpoint(first))
        .with(all.length - 1, () => formatEndpoint(last))
        .otherwise(() => ''),
    )
  }, [hourlyCosts])

  const toolTipCallback = useCallback(
    (context: TooltipItem<'bar'>) => {
      const bar = hourlyCosts[context.dataIndex]
      if (!bar) {
        return ''
      }
      const cost = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        // Sub-cent hours are real on cheap rates — don't hide the tail
        minimumFractionDigits: 2,
        maximumFractionDigits: 3,
      }).format(bar.cost)
      const hour = dayjs(bar.hour_starting).format('ha').toLowerCase()
      return `${hour} • ${bar.kwh.toFixed(1)} kWh • ${cost}`
    },
    [hourlyCosts, currency],
  )

  // Every stepSize tick gets a $ label; whole-dollar mode drops the cents
  const yAxisTicksCallback = useCallback(
    (tickValue: string | number) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: wholeDollars ? 0 : 2,
      }).format(+tickValue),
    [currency, wholeDollars],
  )

  return (
    <div className="flex flex-col gap-2 w-full">
      <Text variant="body1">Charging Costs by Hour</Text>
      <BarGraph
        labels={labels}
        datasets={datasets}
        yAxisValues={yAxisValues}
        maxYAxisValue={axisTop}
        yAxisStepSize={step}
        chartHeight={chartHeight}
        toolTipCallback={toolTipCallback}
        xAxisTicksCallback={yAxisTicksCallback}
      />
    </div>
  )
}
